import { useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { ChevronRight, Link2 } from 'lucide-react'
import { nativeSportsbookApi } from '@/services/api'
import { americanOdds } from './nativePreviewModel'
import { lineText, selectionLabel } from './playerPropBoardModel'
import {
  availableTabs, builderQuery, builderQueryComplete, builderRequestParams, kickoffLabel, lineCaption, linePresentation, orderedOutcomes, sampleBuilder,
} from './parlayBuilderModel'
import styles from './NativePreview.module.css'
import { isLiveSource } from './catalogSources'

const WHITESPACE_PATTERN = /\s+/

const MESSAGES = {
  incomplete: 'Add player milestones or a game line to this league.',
  loading: 'Loading same game parlays…',
  unavailable: 'Same game parlays are unavailable. Try again shortly.',
  empty: 'No same game parlays posted for this league yet.',
  sample_missing: 'Sample data answers only the provider templates. Rehearse with live data to preview this league.',
}

// Under live data the builder comes from the tenant runtime the app reads; sample
// games answer only the canonical sample requests, never a borrowed builder.
function resolved(query, remote, catalog, live) {
  if (!builderQueryComplete(query)) return { state: 'incomplete' }
  if (catalog?.rehearsal === 'empty') return { state: 'empty' }
  if (catalog?.rehearsal === 'unavailable_inventory' || catalog?.rehearsal === 'unavailable_markets') return { state: 'unavailable' }
  if (!live) {
    const builder = sampleBuilder(query)
    return builder ? { state: builder.state, builder } : { state: 'sample_missing' }
  }
  if (remote?.isError) return { state: 'unavailable' }
  if (!remote?.data) return { state: remote?.isFetching ? 'loading' : 'unavailable' }
  return { state: remote.data.state, builder: remote.data }
}

function builderQueryOptions(query, slug, catalog, live) {
  return {
    queryKey: ['native-parlay-builder', slug, catalog?.source, query],
    enabled: live && builderQueryComplete(query) && Boolean(slug) && !catalog?.rehearsal,
    queryFn: ({ signal }) => nativeSportsbookApi.previewParlayBuilder(slug, builderRequestParams(query), signal, catalog.source),
    staleTime: 5000, refetchInterval: 15000, retry: false,
  }
}

/**
 * While editing, a click on the canvas selects the section and Enter or Space on the
 * section selects it too; a chip's own keys stay with the chip so it still activates.
 */
const keepKeys = (event) => {
  if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
}

/** The first league with a game to build on, as the apps settle on it; an outage stops the search. */
function firstLeague(results) {
  const index = results.findIndex((result) => result.state !== 'empty')
  return index === -1 ? 0 : index
}

function TeamMark({ participant, size }) {
  const presentation = participant?.presentation
  if (!presentation?.primary_color || !presentation?.secondary_color) {
    return <span className={styles.builderInitials} style={{ width: size, height: size }} aria-hidden="true">{(participant?.name || '').split(WHITESPACE_PATTERN).slice(0, 2).map((word) => word[0]).join('')}</span>
  }
  const gradient = `linear-gradient(225deg, ${presentation.secondary_color} 0%, ${presentation.secondary_color} 49%, ${presentation.primary_color} 51%, ${presentation.primary_color} 100%)`
  return <span className={styles.teamBadge} style={{ width: size, height: size, background: gradient }} aria-hidden="true" />
}

function Price({ label, caption, price, suspended }) {
  return (
    <span className={`${styles.odds} ${styles.builderCell}`} data-open={suspended ? 'false' : 'true'} role="button" tabIndex={suspended ? -1 : 0} aria-label={`${label} ${price}`}>
      {caption ? <span className={styles.oddsCaption}>{caption}</span> : null}
      {suspended ? <span className={styles.susp}>SUSP</span> : <span className={styles.oddsValue}>{price}</span>}
    </span>
  )
}

function MilestoneGrid({ group, resolvedGroup }) {
  const columns = group.columns || []
  return (
    <div className={styles.builderGrid} style={{ '--builder-columns': columns.length }}>
      <span className={styles.builderGridHeader}>{group.header}</span>
      {columns.map((column) => <span key={column.id} className={styles.propHeaderLabel}>{column.label}</span>)}
      {resolvedGroup.rows.map((row) => [
        <div key={`${row.event_id}:${row.player.id}`} className={styles.propPlayer}>
          <span className={styles.builderPlayerName}>{row.player.name}</span>
          {row.team ? <span className={styles.propPlayerTeam}>{row.team.name}</span> : null}
        </div>,
        ...row.cells.map((cell, index) => {
          const key = `${row.event_id}:${row.player.id}:${columns[index]?.id ?? index}`
          if (!cell) return <span key={key} className={`${styles.propEmptyCell} ${styles.builderCell}`} aria-label={`${row.player.name} ${columns[index]?.label ?? ''}, not offered`}>—</span>
          const suspended = cell.status === 'SUSPENDED' || cell.market_status === 'SUSPENDED'
          const selection = resolvedGroup.columns[index]?.selection
          // An over/under column on each player's main line names the line it prices.
          const caption = (selection === 'over' || selection === 'under') && cell.line_millis != null ? lineText(cell.line_millis) : null
          return <Price key={key} label={selectionLabel(row, resolvedGroup.columns[index], columns[index]?.label, cell)} caption={caption} price={cell.odds_american || americanOdds(cell.odds_millis)} suspended={suspended} />
        }),
      ])}
    </div>
  )
}

function GameLine({ line, event }) {
  const presentation = linePresentation(line)
  const suspended = line.status === 'SUSPENDED'
  return (
    <div className={styles.builderLines}>
      {orderedOutcomes(line, event).map((outcome) => {
        const team = event.participants.find((participant) => participant.id === outcome.participant_id)
        const caption = lineCaption(outcome, presentation)
        const label = team?.name || outcome.label
        return (
          <div key={outcome.outcome_id} className={styles.builderLineRow}>
            {team ? <TeamMark participant={team} size={26} /> : null}
            <span className={styles.builderLineLabel}>{label}</span>
            <Price label={`${label}${caption ? ` ${caption}` : ''} · ${line.market_label}`} caption={caption} price={outcome.odds_american || americanOdds(outcome.odds_millis)} suspended={suspended || outcome.status === 'SUSPENDED'} />
          </div>
        )
      })}
    </div>
  )
}

export function ParlayBuilderPreview({ component, catalog, slug, interactive }) {
  const leagues = component.leagues || []
  const live = isLiveSource(catalog?.source)
  // The chosen league is kept by identity, so reordering leagues in the inspector keeps it.
  const [choice, setChoice] = useState({ leagueId: null, events: {}, tab: null, group: null })
  const baseQueries = leagues.map((league) => builderQuery(component, league))
  const remotes = useQueries({ queries: baseQueries.map((query) => builderQueryOptions(query, slug, catalog, live)) })
  const bases = baseQueries.map((query, index) => resolved(query, remotes[index], catalog, live))
  const chosenIndex = leagues.findIndex((item) => item.id === choice.leagueId)
  const leagueIndex = chosenIndex === -1 ? firstLeague(bases) : chosenIndex
  const league = leagues[leagueIndex]
  const eventId = league ? choice.events[league.id] : undefined
  const eventQuery = league && eventId ? builderQuery(component, league, eventId) : null
  const eventRemote = useQuery({ ...builderQueryOptions(eventQuery || {}, slug, catalog, live), enabled: Boolean(eventQuery) && live && builderQueryComplete(eventQuery) && Boolean(slug) && !catalog?.rehearsal })
  const current = eventQuery ? resolved(eventQuery, eventRemote, catalog, live) : bases[leagueIndex] || { state: 'incomplete' }
  const everyEmpty = bases.length > 0 && bases.every((result) => result.state === 'empty')

  if (everyEmpty && component.empty_behavior === 'hide') {
    return interactive ? <div className={styles.placeholder}>{component.title}: hidden while no league has a game to build on.</div> : null
  }
  const builder = current.state === 'ready' ? current.builder : null
  const baseBuilder = bases[leagueIndex]?.state === 'ready' ? bases[leagueIndex].builder : null
  // A game whose read fails keeps the league's game chips, so another game can be chosen.
  const picker = builder || baseBuilder
  const selectedEventId = builder?.event_id ?? eventId ?? picker?.event_id
  const event = builder?.events.find((item) => item.event_id === builder.event_id)
  const tabs = availableTabs(builder)
  const tab = tabs.includes(choice.tab) ? choice.tab : tabs[0]
  const groups = league?.milestones?.groups || []
  const postedGroups = builder ? groups.map((_, index) => index).filter((index) => builder.groups[index]?.rows.length) : []
  const groupIndex = postedGroups.includes(choice.group) ? choice.group : postedGroups[0]
  const tabLabel = (key) => key === 'milestones' ? league?.milestones?.label : league?.game_lines?.[Number(key.slice(5))]?.label
  const update = (patch) => setChoice((previous) => ({ ...previous, ...patch }))
  const source = builder?.source || bases.find((result) => result.builder)?.builder?.source

  return (
    <div className={`${styles.card} ${styles.builder}`} data-testid="parlay-builder">
      <div className={styles.builderHeader}>
        <span className={styles.builderTile} aria-hidden="true"><Link2 size={18} /></span>
        <div className={styles.builderTitles}>
          <span className={styles.builderTitle}>{component.title}</span>
          {component.subtitle ? <span className={styles.builderSubtitle}>{component.subtitle}</span> : null}
        </div>
        {event ? <span className={styles.builderViewAll} aria-label={`View all markets for ${event.name}`}>VIEW ALL<ChevronRight size={10} aria-hidden="true" /></span> : null}
      </div>
      {source === 'fixture' ? <div className={styles.fixtureNotice}>Fixture markets · Not live inventory</div> : null}
      {everyEmpty ? <div className={styles.placeholder}>No same game parlays are posted right now.</div> : <>
        {leagues.length > 1 ? (
          <div className={styles.builderChips}>
            {leagues.map((item, index) => (
              <button key={item.id} type="button" className={styles.builderLeagueChip} aria-pressed={index === leagueIndex} data-empty={bases[index]?.state === 'empty' ? 'true' : 'false'}
                aria-label={bases[index]?.state === 'empty' ? `${item.label}, no games posted` : item.label} onKeyDown={keepKeys} onClick={() => update({ leagueId: item.id })}>{item.label}</button>
            ))}
          </div>
        ) : null}
        {picker ? (
          <div className={styles.builderChips}>
            {picker.events.map((game) => {
              const away = game.participants.find((participant) => participant.role === 'away')
              const home = game.participants.find((participant) => participant.role === 'home')
              return (
                <button key={game.event_id} type="button" className={styles.builderGameChip} aria-pressed={game.event_id === selectedEventId} aria-label={game.name} onKeyDown={keepKeys}
                  onClick={() => update({ events: { ...choice.events, [league.id]: game.event_id === baseBuilder?.event_id ? undefined : game.event_id } })}>
                  {[away, home].map((team, index) => team ? (
                    <span key={team.id} className={styles.builderTeamLine}><TeamMark participant={team} size={14} />{index ? `@ ${team.name}` : team.name}</span>
                  ) : null)}
                  <span className={styles.builderKickoff}>{kickoffLabel(game.start_time)}</span>
                </button>
              )
            })}
          </div>
        ) : null}
        {builder ? <>
          {tabs.length > 1 ? (
            <div className={styles.builderTabs}>
              {tabs.map((key) => <button key={key} type="button" className={styles.builderTab} aria-pressed={key === tab} onKeyDown={keepKeys} onClick={() => update({ tab: key })}>{tabLabel(key)}</button>)}
            </div>
          ) : null}
          {tab === 'milestones' && groupIndex !== undefined ? <>
            {postedGroups.length > 1 ? (
              <div className={styles.builderStats}>
                {postedGroups.map((index) => <button key={groups[index].id} type="button" className={styles.builderStat} aria-pressed={index === groupIndex} onKeyDown={keepKeys} onClick={() => update({ group: index })}>{groups[index].label}</button>)}
              </div>
            ) : null}
            <MilestoneGrid group={groups[groupIndex]} resolvedGroup={builder.groups[groupIndex]} />
          </> : null}
          {tab?.startsWith('line:') && event && builder.game_lines[Number(tab.slice(5))] ? <GameLine line={builder.game_lines[Number(tab.slice(5))]} event={event} /> : null}
        </> : (
          <div className={current.state === 'unavailable' ? `${styles.placeholder} ${styles.propMessage}` : styles.placeholder} aria-busy={current.state === 'loading' || undefined}>
            <div>{MESSAGES[current.state] || MESSAGES.unavailable}</div>
            {current.state === 'unavailable' ? <span className={styles.betwireAction} aria-hidden="true">Retry</span> : null}
          </div>
        )}
      </>}
    </div>
  )
}
